import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment, ListSubheader } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { debounce } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Pill from './Pill';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  '& .MuiFormLabel-root': {
    fontWeight: 500,
    fontSize: '14px',
    color: theme.palette.secondary.light,
    marginBottom: '8px',
    '&.Mui-focused': {
      color: theme.palette.secondary.light,
    },
  },
}));

const StyledSelect = styled(Select)<{ open?: boolean }>(({ theme, open }) => {
  const tokens = theme.componentSizes.dropdown.md;
  return {
    borderRadius: tokens.borderRadius,
    background: theme.palette.dropdown.bg,
    width: '100%',
    padding: tokens.padding,
    height: tokens.height,
    minHeight: tokens.height,
    boxSizing: 'border-box',
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      fontWeight: 400,
      fontSize: tokens.fontSize,
      color: theme.palette.dropdown.font,
      padding: 0,
      height: '100% !important',
      lineHeight: '21px',
      letterSpacing: '0px',
      boxSizing: 'border-box',
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& fieldset': {
      border: `1px solid ${theme.palette.dropdown.border}`,
      borderRadius: tokens.borderRadius,
      transition: theme.transitions.custom.fast,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.dropdown.border,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.dropdown.focusBorder,
      borderWidth: '1px',
    },
    '& .MuiSelect-icon': {
      color: theme.palette.dropdown.icon,
      fontSize: '20px',
      right: '16px',
      transition: theme.transitions.custom.fast,
      transform: open ? 'rotate(180deg)' : 'rotate(0)',
    },
    '&.Mui-focused': {
      outline: 'none',
    },
    "& .MuiSelect-select[data-value='']": {
      color: theme.palette.dropdown.placeholder,
    },
  };
});

const StyledMenuItem = styled(MenuItem)(({ theme }) => {
  const tokens = theme.componentSizes.dropdown.md;
  return {
    fontSize: tokens.fontSize,
    color: theme.palette.dropdown.font,
    height: tokens.height,
    padding: tokens.padding,
    lineHeight: '21px',
    letterSpacing: '0px',
    '&:hover': {
      backgroundColor: theme.palette.dropdown.itemHoverBg,
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.dropdown.itemSelectedBg,
      color: theme.palette.dropdown.font,
      '&:hover': {
        backgroundColor: theme.palette.dropdown.itemSelectedBg,
      },
    },
  };
});

interface DropdownOption {
  value: string | number;
  label: string;
}

interface DropdownProps {
  label?: string;
  value: string | number | Array<string | number>;
  onChange: (_event: any) => void;
  options?: DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  sx?: object;
  multiSelect?: boolean;
  hasSearch?: boolean;
  debounceMs?: number;
  maxDisplayedOptions?: number;
  noSort?: boolean;
  [key: string]: any;
}

const Dropdown: React.FC<DropdownProps> = React.memo(function Dropdown({
  label,
  value = '',
  onChange,
  options = [],
  placeholder,
  disabled = false,
  error = false,
  helperText,
  sx = {},
  multiSelect = false,
  hasSearch = true,
  debounceMs = 300,
  maxDisplayedOptions = 100,
  noSort = false,
  height,
  selectContainerSx = {},
  ...props
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const defaultHeight = theme.componentSizes.dropdown.md.height;
  const effectiveHeight = height ?? defaultHeight;
  const displayPlaceholder = placeholder ?? t('common.select');
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const selectWrapperRef = React.useRef<HTMLDivElement | null>(null);
  // Normalize value to never be undefined to satisfy MUI Select expectations
  const safeValue = React.useMemo<any>(
    () => value ?? (multiSelect ? [] : ''),
    [value, multiSelect],
  );

  const [menuWidth, setMenuWidth] = React.useState<number | undefined>(502);
  const [search, setSearch] = React.useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = React.useState<string>('');
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const searchInputId = React.useId();

  // Create debounced search function
  const debouncedSetSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
      }, debounceMs),
    [debounceMs],
  );

  // Update debounced search when search changes
  React.useEffect(() => {
    debouncedSetSearch(search);
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [search, debouncedSetSearch]);

  // Allow the select control to grow when multiple chips are rendered
  const selectSx = React.useMemo(
    () =>
      multiSelect
        ? {
            height: '100%',
            minHeight: effectiveHeight,
            alignItems: 'flex-start',
            '& .MuiSelect-select': {
              height: '100%',
              minHeight: effectiveHeight,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
              py: 0.5,
            },
          }
        : {},
    [multiSelect, effectiveHeight],
  );

  React.useEffect(() => {
    // Measure the actual select trigger wrapper instead of the outer Box
    const el = selectWrapperRef.current || containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        setMenuWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const filteredOptions = React.useMemo(() => {
    // Respect caller order if noSort is true
    const base = noSort
      ? [...options]
      : [...options].sort((a, b) => String(a.label).localeCompare(String(b.label)));

    if (!debouncedSearch) {
      // If no search, return limited options for performance
      return base.slice(0, maxDisplayedOptions);
    }

    const q = debouncedSearch.toLowerCase();
    const filtered = base.filter(
      (o) => String(o.label).toLowerCase().includes(q) || String(o.value).toLowerCase().includes(q),
    );

    const sorted = filtered.sort((a, b) => {
      const aLabel = String(a.label).toLowerCase();
      const bLabel = String(b.label).toLowerCase();
      const aValue = String(a.value).toLowerCase();
      const bValue = String(b.value).toLowerCase();

      // Check for exact matches
      const aExactMatch = aLabel === q || aValue === q;
      const bExactMatch = bLabel === q || bValue === q;
      if (aExactMatch && !bExactMatch) return -1;
      if (!aExactMatch && bExactMatch) return 1;

      // Check for prefix matches
      const aPrefixMatch = aLabel.startsWith(q) || aValue.startsWith(q);
      const bPrefixMatch = bLabel.startsWith(q) || bValue.startsWith(q);
      if (aPrefixMatch && !bPrefixMatch) return -1;
      if (!aPrefixMatch && bPrefixMatch) return 1;

      // Both are partial matches or same match type, maintain original order
      return 0;
    });

    // Limit results for performance
    return sorted.slice(0, maxDisplayedOptions);
  }, [options, debouncedSearch, maxDisplayedOptions, noSort]);

  return (
    <Box ref={containerRef} sx={{ width: '100%', ...sx }}>
      <StyledFormControl
        variant="outlined"
        size="medium"
        disabled={disabled}
        error={error}
        fullWidth
      >
        {label && (
          <InputLabel
            shrink={false}
            sx={{
              position: 'static',
              transform: 'none',
              marginBottom: '8px',
              fontWeight: 500,
              fontSize: '14px',
              color: theme.palette.secondary.light,
              lineHeight: '20px',
            }}
          >
            {label}
          </InputLabel>
        )}
        <div ref={selectWrapperRef} style={{ width: '100%' }}>
          <StyledSelect
            value={safeValue}
            onChange={onChange}
            displayEmpty
            disabled={disabled}
            error={error}
            IconComponent={ArrowDropDownRoundedIcon}
            multiple={multiSelect}
            autoWidth={false}
            sx={{ ...selectSx, ...selectContainerSx }}
            onOpen={() => {
              // Focus the search field when menu opens
              setTimeout(() => searchInputRef.current?.focus(), 0);
            }}
            MenuProps={{
              disableAutoFocusItem: true,
              autoFocus: false,
              keepMounted: true,
              MenuListProps: {
                autoFocusItem: false,
              },
              PaperProps: {
                sx: {
                  mt: 0.5,
                  boxShadow: theme.customShadows.dropdown,
                  border: `1px solid ${theme.palette.dropdown.menuBorder}`,
                  maxHeight: 240,
                  minWidth: `${menuWidth}px  !important`,
                  maxWidth: `${menuWidth}px  !important`,
                  '& .MuiList-root': {
                    padding: 0,
                  },
                  '& .MuiMenu-list': {
                    padding: 0,
                    fontSize: '10px',
                  },
                },
              },
            }}
            renderValue={(selected: any) => {
              if (multiSelect) {
                const arr = Array.isArray(selected) ? selected : [];
                if (arr.length === 0) {
                  return (
                    <span style={{ color: theme.palette.dropdown.placeholder }}>
                      {displayPlaceholder}
                    </span>
                  );
                }
                const chipsToShow = arr.slice(0, 3);
                const overflowCount = arr.length - chipsToShow.length;
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      flexWrap: 'wrap',
                      py: 0.5,
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {chipsToShow.map((val) => {
                      const opt = options?.find((o) => o?.value === val);
                      const label = opt?.label || String(val);
                      return (
                        <Box
                          key={String(val)}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <Pill
                            label={label}
                            sx={{ height: 28, fontSize: '12px' }}
                            onDelete={(e: React.SyntheticEvent) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = arr.filter((v) => v !== val);
                              onChange({
                                target: { value: newValue },
                              } as any);
                            }}
                          />
                        </Box>
                      );
                    })}
                    {overflowCount > 0 && (
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <Pill label={`+${overflowCount}`} sx={{ height: 28, fontSize: '12px' }} />
                      </Box>
                    )}
                  </Box>
                );
              } else {
                if (!selected && selected !== 0) {
                  return (
                    <span style={{ color: theme.palette.dropdown.placeholder }}>
                      {displayPlaceholder}
                    </span>
                  );
                }
                const selectedOption = options?.find((option) => option?.value === selected);
                return selectedOption?.label || String(selected);
              }
            }}
            {...props}
          >
            {hasSearch ? (
              <ListSubheader
                style={{
                  position: 'sticky',
                  top: 0,
                  background: theme.palette.dropdown.bg,
                  zIndex: 1,
                  paddingTop: '10px',
                }}
              >
                <TextField
                  id={searchInputId}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  placeholder={t('common.search')}
                  fullWidth
                  inputRef={searchInputRef}
                  onKeyDown={(e) => {
                    // Prevent Select menu from handling keys like Home/End which can close the menu
                    e.stopPropagation();
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  InputProps={{
                    style: { height: '100%', fontSize: 12 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </ListSubheader>
            ) : null}

            {filteredOptions.map((option) => (
              <StyledMenuItem key={option.value} value={option.value}>
                {option.label}
              </StyledMenuItem>
            ))}

            {debouncedSearch &&
            filteredOptions.length === maxDisplayedOptions &&
            options.length > maxDisplayedOptions ? (
              <StyledMenuItem disabled>
                <Box
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.dropdown.placeholder,
                    fontStyle: 'italic',
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  Showing first {maxDisplayedOptions} results. Type more to narrow down.
                </Box>
              </StyledMenuItem>
            ) : null}

            {!debouncedSearch && options.length > maxDisplayedOptions ? (
              <StyledMenuItem disabled>
                <Box
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.dropdown.placeholder,
                    fontStyle: 'italic',
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  Showing first {maxDisplayedOptions} of {options.length} options. Type to search.
                </Box>
              </StyledMenuItem>
            ) : null}
          </StyledSelect>
        </div>
        {helperText && (
          <Box
            sx={{
              fontSize: '12px',
              color: error
                ? theme.palette.dropdown.helperTextError
                : theme.palette.dropdown.helperTextDefault,
              marginTop: '4px',
              marginLeft: '12px',
            }}
          >
            {helperText}
          </Box>
        )}
      </StyledFormControl>
    </Box>
  );
});

export default Dropdown;
